/**
 * Notification Settings Component
 * 
 * Manages user notification preferences for various app events
 * including service reminders, team schedules, and system updates.
 */

import React, { useState } from 'react';
import {
  Box,
  Grid,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import useCustomFetch from '../../hooks/useCustomFetch';

function NotificationSettings() {
  const customFetch = useCustomFetch();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState({
    serviceReminders: true,
    teamSchedule: true,
    newSongs: true,
    emailNotifications: true,
    pushNotifications: true,
    rehearsalUpdates: true,
    songChanges: true,
    teamChanges: true
  });

  const handleToggle = (name) => (event) => {
    setPreferences(prev => ({
      ...prev,
      [name]: event.target.checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await customFetch('users/notifications', {
        method: 'PUT',
        body: JSON.stringify(preferences)
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to update notification preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Service Notifications */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Service Notifications
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.serviceReminders}
                  onChange={handleToggle('serviceReminders')}
                />
              }
              label="Service Reminders"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.rehearsalUpdates}
                  onChange={handleToggle('rehearsalUpdates')}
                />
              }
              label="Rehearsal Updates"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Team Notifications */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Team Notifications
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.teamSchedule}
                  onChange={handleToggle('teamSchedule')}
                />
              }
              label="Team Schedule Updates"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.teamChanges}
                  onChange={handleToggle('teamChanges')}
                />
              }
              label="Team Member Changes"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Song Notifications */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Song Notifications
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.newSongs}
                  onChange={handleToggle('newSongs')}
                />
              }
              label="New Songs Added"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.songChanges}
                  onChange={handleToggle('songChanges')}
                />
              }
              label="Song Updates"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Notification Methods */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Notification Methods
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications}
                  onChange={handleToggle('emailNotifications')}
                />
              }
              label="Email Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.pushNotifications}
                  onChange={handleToggle('pushNotifications')}
                />
              }
              label="Push Notifications"
            />
          </FormGroup>
        </Grid>

        {/* Feedback Messages */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        {success && (
          <Grid item xs={12}>
            <Alert severity="success">Notification preferences updated successfully!</Alert>
          </Grid>
        )}

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Preferences'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default NotificationSettings;
